import { isCustomDomain as checkCustomDomain } from '~/lib/domainResolver';

/**
 * useDomainResolver
 *
 * Composable for detecting custom tenant domains and resolving the
 * associated tenant from Firestore.
 *
 * Usage:
 * ```ts
 * const { getHostname, isCustomDomain } = useDomainResolver();
 * if (isCustomDomain(getHostname())) {
 *   const tenantId = await tenantStore.resolveTenantByDomain(getHostname());
 * }
 * ```
 */
export const useDomainResolver = () => {
    const config = useRuntimeConfig();
    const customDomainTenantCache = useState<Record<string, string>>('custom-domain-tenant-cache', () => ({}));

    /**
     * Returns the hostname of the current request.
     * Uses Nuxt's `useRequestURL()` which works in both SSR and SPA contexts.
     */
    const getHostname = (): string => {
        return useRequestURL().hostname;
    };

    /**
     * Determines whether the given hostname is a custom tenant domain
     * (not the default application domain or a local development hostname).
     *
     * @param hostname - The hostname to evaluate.
     * @returns `true` if this is a custom branded domain.
     */
    const isCustomDomain = (hostname: string): boolean => {
        const appDomain = config.public.appDomain as string | undefined;
        return checkCustomDomain(hostname, appDomain);
    };

    /**
     * Resolves a verified custom hostname through the public server endpoint.
     * Portal users are intentionally not required to have tenant-member
     * Firestore access in order to establish their branded portal context.
     */
    const resolveCustomDomainTenantId = async (hostname: string): Promise<string | null> => {
        const normalizedHostname = String(hostname || '').trim().toLowerCase();
        if (!normalizedHostname || !isCustomDomain(normalizedHostname)) {
            return null;
        }

        const cachedTenantId = customDomainTenantCache.value[normalizedHostname];
        if (cachedTenantId) {
            return cachedTenantId;
        }

        try {
            const response = await $fetch<{ tenant?: { id?: string } | null }>('/api/public/tenant-branding', {
                query: { hostname: normalizedHostname },
            });

            const tenantId = String(response.tenant?.id || '').trim() || null;
            if (tenantId) {
                customDomainTenantCache.value[normalizedHostname] = tenantId;
            }
            return tenantId;
        } catch (error) {
            console.warn('Unable to resolve the client portal custom domain:', error);
            return null;
        }
    };

    return { getHostname, isCustomDomain, resolveCustomDomainTenantId };
};
